import { IconButtonSizeEnum } from '@acceso/design-system';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BroadcasterScheduleProgramMiniCardCardActions } from '@/components/BroadcasterScheduleProgramMiniCardBase/components/BroadcasterScheduleProgramMiniCardCardActions';
import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { TProgramBroadcastSegmentsPatch } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchSegment } from '@/features/segments/api/patch-segment';
import { downloadFromUrl } from '@/helpers/download';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useReport } from '@/stores/report-store';

import { usePatchRelaunchSegment } from '../../api/patch-relaunch-segment';
import { TReviewStateValues, TSegment } from '../../types';
import { SegmentsDetailHeaderInfo } from '../SegmentsDetailHeaderInfo/SegmentsDetailHeaderInfo';

export function SegmentsDetailCardHeader() {
  const { t } = useTranslation();
  const { activeSegment, setActiveSegment, setSegmentItems } = useBroadcastScheduleProgram();
  const { id = '' } = useParams();
  const { mutateAsync: patchSegment } = usePatchSegment({ id });
  const { setReport, setReportedId, setReportType } = useReport();
  const relaunchDialog = useDisclosureWithData<TSegment>();
  const { mutate } = usePatchRelaunchSegment();

  const handleStatusChange = async (status: string) => {
    if (status === '5') {
      setReportType('segment');
      setReport(true);
      setReportedId(activeSegment?.id || '');
    }
    const body: TProgramBroadcastSegmentsPatch = {
      ...activeSegment,
      id: activeSegment?.id || '',
      review_state: parseInt(status) as TReviewStateValues,
      uri: activeSegment?.uri || '',
      channel_id: activeSegment?.channel_id || '',
      media_type: activeSegment?.media_type || '',
      start_datetime: activeSegment?.start_datetime || '',
      end_datetime: activeSegment?.end_datetime || '',
      duration_in_seconds: activeSegment?.duration_in_seconds || 0,
      language_id: activeSegment?.language_id || '',
      programs: activeSegment?.programs.map(program => program.id) || [],
      segment_mentions: activeSegment?.segment_mentions || [],
      segment_transcriptions: activeSegment?.segment_transcriptions || [],
      segment_logos: activeSegment?.segment_logos || [],
      segment_peoples: activeSegment?.segment_peoples || [],
    };

    await patchSegment(body).then(data => {
      setActiveSegment(prev => {
        if (!prev) return prev;
        return { ...prev, review_state: data.review_state };
      });
      setSegmentItems(prev =>
        prev.map(item => {
          if (item.id === activeSegment?.id) {
            return { ...item, review_state: data.review_state };
          }
          return item;
        })
      );
    });
  };

  if (!activeSegment) return <></>;

  return (
    <div className="flex gap-1">
      <SegmentsDetailHeaderInfo segment={activeSegment} />
      <div className="flex items-center justify-end">
        <StatusSeenChipSelect
          value={activeSegment?.review_state || 0}
          onChange={handleStatusChange}
          asIcon
          listOrientation="left"
          reviewStates={SEGMENT_REVIEW_STATES}
        />
        <BroadcasterScheduleProgramMiniCardCardActions
          item={activeSegment}
          onAi={() => relaunchDialog.open(activeSegment)}
          onDownload={downloadFromUrl}
          size={IconButtonSizeEnum.SM}
        />
      </div>

      <RelaunchEnrichmentsModal
        isOpen={relaunchDialog.isOpen}
        onClose={relaunchDialog.close}
        onAction={enrichments => {
          const toId = relaunchDialog.data?.id || '';
          if (!toId) return;
          mutate({ id: toId, enrichments });
          relaunchDialog.close();
        }}
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        allowAdvertising={false}
        isRadio={activeSegment?.media_type === 'rts'}
      />
    </div>
  );
}
